import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import MedicalDocumentsPage from './pages/MedicalDocumentsPage';
import AdministrativeDocumentsPage from './pages/AdministrativeDocumentsPage';
import ProtectedRoute from './ProtectedRoute';
import UserManagementPage from './pages/UserManagementPage';
import UserRegisterPage from './components/Users/UserRegisterPage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import SendDocumentPage from './pages/SendDocumentPage';
import ReportsPage from './pages/ReportsPage';
import PatientAccessPage from './pages/PatientAccessPage';
import SelfieCapturePage from './pages/SelfieCapturePage';
import PlantillaEditorPage from './pages/PlantillaEditorPage';
import PlantillasPage from './pages/PlantillasPage';
import DocumentViewPage from './pages/DocumentViewPage';
import SignatureCapturePage from './pages/SignatureCapturePage';
import 'bootstrap-icons/font/bootstrap-icons.css';

// Componente detector de errores en rutas
function RouteErrorBoundary() {
  const location = useLocation();
  
  useEffect(() => {
    console.log('🌐 RUTA ACTUAL:', location.pathname);
    console.log('📦 STATE:', location.state);
    console.log('---');
  }, [location]);
  
  return null;
}

function App() {
  useEffect(() => {
    // Gestión de seguridad de sesión
    
    // Funcionalidad 1: Cerrar sesión al cerrar ventana/pestaña
    const handleBeforeUnload = (event) => {
      localStorage.clear();
      event.preventDefault();
      event.returnValue = '¿Estás seguro de que quieres salir? Tu sesión se cerrará.';
      
      navigator.sendBeacon('/api/logout', JSON.stringify({ 
        action: 'window_close' 
      }));
    };

    // Funcionalidad 2: Inactividad de 90 minutos
    let inactivityTimer;
    let warningTimer;
    let lastActivity = Date.now();
    const INACTIVITY_TIME = 90 * 60 * 1000; // 90 minutos
    const WARNING_TIME = 85 * 60 * 1000;   // 85 minutos

    const resetTimer = () => {
      lastActivity = Date.now();
      clearTimeout(inactivityTimer);
      clearTimeout(warningTimer);
      
      inactivityTimer = setTimeout(() => {
        alert('Tu sesión ha expirado por inactividad (90 minutos). Serás redirigido al login.');
        localStorage.clear();
        window.location.href = 'https://biofirmabk.colombianadetrasplantes.com/auth/logout';
      }, INACTIVITY_TIME);

      warningTimer = setTimeout(() => {
        const confirmStay = window.confirm(
          'Tu sesión expirará en 5 minutos por inactividad. ¿Quieres mantener la sesión activa?'
        );
        
        if (confirmStay) {
          resetTimer();
        }
      }, WARNING_TIME);
    };

    // Funcionalidad 3: Detectar actividad del usuario
    const activityEvents = [
      'mousedown', 
      'mousemove', 
      'keypress', 
      'scroll', 
      'touchstart', 
      'click',
      'keydown',
      'touchmove'
    ];
    
    const handleActivity = () => {
      resetTimer();
    };

    // Inicializar eventos solo si hay sesión activa
    const hasActiveSession = localStorage.getItem('token') || 
                            localStorage.getItem('nombres') || 
                            localStorage.getItem('correo');

    if (hasActiveSession) {
      console.log('🔒 Gestión de seguridad de sesión activada');
      
      window.addEventListener('beforeunload', handleBeforeUnload);
      
      activityEvents.forEach(event => {
        document.addEventListener(event, handleActivity, true);
      });
      
      resetTimer();
    }

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      clearTimeout(inactivityTimer);
      clearTimeout(warningTimer);
    };
  }, []);

  return (
    <Router>
      <RouteErrorBoundary />
      <Routes>
        {/* Ruta pública por defecto */}
        <Route path="/" element={<LoginPage />} />
        <Route path="/login" element={<LoginPage />} />

        {/* Rutas protegidas del dashboard */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'enfermeria', 'administrativo']}>
              <DashboardPage />
            </ProtectedRoute>
          }
        />

        {/* Gestión de plantillas */}
<Route
  path="/plantillas"
  element={
    <ProtectedRoute allowedRoles={['superadmin']}>
      <PlantillasPage />
    </ProtectedRoute>
  }
/>

{/*  Editor de posición de firma en plantillas */}
<Route
  path="/plantillas/:id/editor"
  element={
    <ProtectedRoute allowedRoles={['superadmin']}>
      <PlantillaEditorPage />
    </ProtectedRoute>
  }
/>
        
        <Route
          path="/send-document"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'enfermeria', 'administrativo']}>
              <SendDocumentPage />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/reports"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'enfermeria', 'administrativo']}>
              <ReportsPage />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/register"
          element={
            <ProtectedRoute allowedRoles={['superadmin']}>
              <UserRegisterPage />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/user-management"
          element={
            <ProtectedRoute allowedRoles={['superadmin']}>
              <UserManagementPage />
            </ProtectedRoute>
          }
        />

        {/* ⭐ NUEVA RUTA: Editor de posición de firma en plantillas */}
        <Route
          path="/plantillas/:id/editor"
          element={
            <ProtectedRoute allowedRoles={['superadmin']}>
              <PlantillaEditorPage />
            </ProtectedRoute>
          }
        />

        {/* Rutas públicas para pacientes */}
        <Route path="/patient-access" element={<PatientAccessPage />} />
        <Route path="/selfie-capture/:cedula" element={<SelfieCapturePage />} />
        <Route path="/document-view/:cedula" element={<DocumentViewPage />} />
        <Route path="/firmar/:id" element={<SignatureCapturePage />} />
        <Route path="/ver-documento" element={<DocumentViewPage />} />
        <Route path="/medical-documents" element={<MedicalDocumentsPage />} />
        <Route path="/administrative-documents" element={<AdministrativeDocumentsPage />} />
      </Routes>
    </Router>
  );
}

export default App;